import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Routes where the Zoho SalesIQ live-chat launcher is allowed to be visible.
// These are the public marketing pages only. Every other route keeps the
// launcher hidden — the authenticated app (/create, /dashboard, /get-credits)
// and the /reset-password recovery deep-link, plus any unknown path.
//
// To let the widget onto a future marketing page, add its path here. Entries
// are matched exactly (after trailing-slash normalisation); if a future
// section has sub-routes, switch its entry to a prefix test.
const PUBLIC_CHAT_PATHS = ['/', '/pricing', '/story', '/privacy', '/terms', '/contact', '/support'];

// Widget code is passed as the `wc` query param on the loader URL. It is NOT
// a secret (it is visible in page source on every public page); it lives in
// an env var purely so the widget can be kill-switched and left out of local
// dev. When it is unset this component does nothing at all.
const WIDGET_CODE = process.env.REACT_APP_ZOHO_SALESIQ_WIDGETCODE;
const LOADER_BASE = process.env.REACT_APP_ZOHO_SALESIQ_SRC || 'https://salesiq.zohopublic.com/widget';

function normalizePath(pathname) {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function isChatAllowed(pathname) {
  return PUBLIC_CHAT_PATHS.indexOf(normalizePath(pathname)) !== -1;
}

// Injects the two Zoho script tags, once. Tag 1 is a small inline bootstrap
// we author ourselves (rather than pasting Zoho's verbatim) so we can install
// a ready handler that reads the live pathname when the widget finishes
// loading — that covers the visitor navigating to a non-public route while
// the CDN fetch is still in flight. Because tag 1 has no src it executes
// synchronously the instant it is appended, so it is guaranteed to have run
// before tag 2 (the loader) is inserted.
function injectWidget(cdnFailedRef) {
  const bootstrap = document.createElement('script');
  bootstrap.id = 'zsiqchat';
  bootstrap.type = 'text/javascript';
  bootstrap.text =
    'window.$zoho=window.$zoho||{};' +
    'window.$zoho.salesiq=window.$zoho.salesiq||{ready:function(){}};' +
    'window.$zoho.salesiq.ready=function(){' +
      'try{' +
        'var allow=' + JSON.stringify(PUBLIC_CHAT_PATHS) + ';' +
        'var p=window.location.pathname;' +
        'if(p.length>1){p=p.replace(/\\/+$/,"");}' +
        'window.$zoho.salesiq.floatbutton.visible(allow.indexOf(p)!==-1?"show":"hide");' +
      '}catch(e){}' +
    '};';
  document.body.appendChild(bootstrap);

  const loader = document.createElement('script');
  loader.id = 'zsiqscript';
  loader.defer = true;
  loader.src = LOADER_BASE + '?wc=' + encodeURIComponent(WIDGET_CODE);
  loader.onerror = function () {
    if (cdnFailedRef.current) return;
    cdnFailedRef.current = true;
    // Single line, never repeated — a slow/unreachable CDN must not spam.
    console.warn('[SalesIQ] live-chat widget failed to load');
  };
  document.body.appendChild(loader);
}

/**
 * SalesIQWidget — lazily loads the Zoho SalesIQ live-chat widget the first
 * time the visitor is on a public marketing route, then shows/hides the float
 * button on every subsequent route change via Zoho's documented
 * $zoho.salesiq.floatbutton.visible("show" | "hide") API.
 *
 * Renders nothing. Mounted once in App.js inside <Router> but outside
 * <Routes>, so it lives for the whole session and the widget is loaded at
 * most once and never torn down.
 *
 * Total no-op when REACT_APP_ZOHO_SALESIQ_WIDGETCODE is unset: no injection,
 * no API calls, no console output.
 */
export default function SalesIQWidget() {
  const { pathname } = useLocation();
  const injectedRef = useRef(false);
  const cdnFailedRef = useRef(false);

  useEffect(() => {
    if (!WIDGET_CODE) return;

    const allowed = isChatAllowed(pathname);

    // (a) First allowed route reached: inject the widget, once.
    if (allowed && !injectedRef.current) {
      injectedRef.current = true;
      if (!document.getElementById('zsiqscript')) {
        injectWidget(cdnFailedRef);
      }
    }

    // (b) Widget injected: toggle the float button to match this route. If
    //     Zoho has not finished loading yet this is a no-op and the ready
    //     handler in tag 1 sets the correct initial state when it fires.
    if (injectedRef.current) {
      const floatbutton =
        window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.floatbutton;
      if (floatbutton && typeof floatbutton.visible === 'function') {
        floatbutton.visible(allowed ? 'show' : 'hide');
      }
    }
  }, [pathname]);

  return null;
}
